import axios from 'axios';
import { API_CONFIG } from '../config/api';
import errorReportingService from './errorReportingService';

/**
 * API Service with secure token management
 * Tokens are stored in memory only (not localStorage) for security
 */

// In-memory token storage (not accessible to XSS attacks)
let accessToken = null;

/**
 * Set the access token for authenticated requests
 * @param {string} token - Auth0 access token
 */
export const setAccessToken = (token) => {
  accessToken = token;
};

/**
 * Clear the access token (on logout)
 */
export const clearAccessToken = () => {
  accessToken = null;
};

/**
 * Get the current access token (for debugging only)
 * @returns {boolean} Whether a token is set
 */
export const hasAccessToken = () => {
  return accessToken !== null;
};

const isFormDataPayload = (data) =>
  typeof FormData !== 'undefined' && data instanceof FormData;

export const stripJsonContentTypeForFormData = (config) => {
  if (!isFormDataPayload(config?.data) || !config.headers) {
    return config;
  }

  if (typeof config.headers.delete === 'function') {
    config.headers.delete('Content-Type');
    config.headers.delete('content-type');
  } else {
    delete config.headers['Content-Type'];
    delete config.headers['content-type'];
  }

  return config;
};
// Create axios instance
export const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.DEFAULT_HEADERS,
});

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    stripJsonContentTypeForFormData(config);

    // Add auth token from memory if available
    if (accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 errors - token might be expired
    // The calling component should handle re-authentication via Auth0
    if (error.response?.status === 401) {
      // Clear in-memory token on unauthorized
      accessToken = null;
      // Don't redirect here - let the Auth0 SDK handle re-authentication
    }

    // Report server errors (5xx) to monitoring
    // Skip 4xx client errors and network errors
    const status = error.response?.status;
    if (status && status >= 500) {
      errorReportingService.reportApiError(error, error.config);
    }

    return Promise.reject(error);
  }
);

export default api;
